import type { CSSProperties, ReactNode } from 'react';
import { useBusinessDay } from '../../../shared/hooks/useBusinessDay.js';
import { useTranslation } from '../../../shared/i18n/index.js';
import {
  approvalLevelLine,
  approvalLevelName,
  approvalLevelState,
  approverNames,
  personLabel,
  type ApprovalLevelState,
  type ApprovalTrailStep,
} from '../../../shared/contracts/approval-trail.js';

export {
  approvalLevelState,
  approverNames,
  personLabel,
  type ApprovalLevelState,
  type ApprovalTrailStep,
};

function levelStyle(state: ApprovalLevelState): CSSProperties {
  // Longhand throughout: a level changes state in place while the page is
  // open, and React warns when a shorthand and its parts are mixed.
  const base: CSSProperties = {
    minWidth: 74,
    textAlign: 'center',
    padding: '0.3rem 0.6rem',
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--color-border, #dadce0)',
    background: 'transparent',
    color: 'var(--color-text-muted, #5f6368)',
  };
  if (state === 'done') {
    return {
      ...base,
      background: 'var(--color-success, #137333)',
      borderColor: 'var(--color-success, #137333)',
      color: '#fff',
    };
  }
  if (state === 'rejected') {
    return {
      ...base,
      background: 'var(--color-danger, #c5221f)',
      borderColor: 'var(--color-danger, #c5221f)',
      color: '#fff',
    };
  }
  if (state === 'current') {
    return {
      ...base,
      borderWidth: 2,
      borderColor: 'var(--color-border-focus, #1a73e8)',
      color: 'var(--color-text, #202124)',
    };
  }
  return base;
}

function connectorStyle(behind: ApprovalLevelState): CSSProperties {
  const colour =
    behind === 'done'
      ? 'var(--color-success, #137333)'
      : behind === 'rejected'
        ? 'var(--color-danger, #c5221f)'
        : 'var(--color-border, #dadce0)';
  return { width: 18, height: 2, background: colour, flex: '0 0 auto' };
}

export interface ApprovalLevelsProps {
  readonly steps: readonly ApprovalTrailStep[];
  /** Adds the per-level "approved by / waiting on" lines under the markers. */
  readonly showDetail?: boolean;
}

/**
 * Where a request has reached, as one marker per approval level.
 *
 * Shared rather than copied per screen: a tracker that says APPROVED on one
 * page and PENDING on another is worse than no tracker, and today's whole
 * class of approval bugs came from the same rule being written twice. How a
 * level is read, and the words for it in the reader's language, live in
 * shared/contracts/approval-trail, for the phone too.
 */
export function ApprovalLevels({
  steps,
  showDetail = true,
}: ApprovalLevelsProps): ReactNode {
  const { t } = useTranslation();
  // Shared by every screen that shows a tracker, so the moment an approval was
  // given must read the same on all of them — and in the format the tenant
  // chose, not the one the reader's browser prefers.
  const { at } = useBusinessDay();
  if (steps.length === 0) return null;
  return (
    <section aria-label="approval-levels" style={{ margin: '1rem 0' }}>
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          listStyle: 'none',
          padding: 0,
          margin: '0 0 0.75rem',
          flexWrap: 'wrap',
        }}
      >
        {steps.map((s, i) => {
          const state = approvalLevelState(s.stepStatus);
          // The joining line takes the colour of the level behind it, so the
          // eye follows one unbroken green run up to wherever the request is.
          const behind = i > 0 ? approvalLevelState(steps[i - 1].stepStatus) : null;
          return (
            <li
              key={s.instanceStepId}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {behind ? (
                <span aria-hidden="true" style={connectorStyle(behind)} />
              ) : null}
              <span
                data-testid={`level-${s.stepSequence}`}
                data-state={state}
                title={`${approvalLevelName(s, t)} — ${s.stepName ?? ''} — ${s.stepStatus}`}
                style={levelStyle(state)}
              >
                {state === 'done' ? '\u2713 ' : ''}
                {approvalLevelName(s, t)}
              </span>
            </li>
          );
        })}
      </ol>
      {showDetail ? (
        <ul
          aria-label="approval-trail"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          {steps.map((s) => {
            const state = approvalLevelState(s.stepStatus);
            const colour =
              state === 'done'
                ? 'var(--color-success, #137333)'
                : state === 'rejected'
                  ? 'var(--color-danger, #c5221f)'
                  : state === 'todo'
                    ? 'var(--color-text-muted, #5f6368)'
                    : undefined;
            return (
              <li key={s.instanceStepId} style={{ padding: '0.25rem 0' }}>
                <strong>{approvalLevelName(s, t)}</strong>
                {s.stepName ? ` — ${s.stepName}` : ''}
                {' — '}
                <span style={colour ? { color: colour } : undefined}>
                  {approvalLevelLine(s, t, at)}
                </span>
                {s.remarks ? (
                  <div
                    style={{
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-muted, #5f6368)',
                    }}
                  >
                    {s.remarks}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
